import React from 'react';
import RGL, {
  WidthProvider,
  type Layout,
  type LayoutItem as RGLLayoutItem,
} from 'react-grid-layout/legacy';
import {
  Box,
  Button,
  Flex,
  Heading,
  LoadingOrbit,
  Menu,
  MenuItem,
  MenuTrigger,
  Text,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { ArrowLeft, DotsThreeVertical, Plus, Trash } from '@phosphor-icons/react';
import { DashboardCardWidget } from './DashboardCard';
import { DashboardCardEditor } from './DashboardCardEditor';
import {
  type DashboardCard,
  type LayoutItem,
  useAddDashboardCard,
  useDashboard,
  useDeleteDashboard,
  useRenameDashboard,
  useUpdateLayout,
} from './useDashboardData';

const ResponsiveGrid = WidthProvider(RGL);

type Props = {
  dashboardId: string;
  onBack: () => void;
};

const COLS = 12;
const ROW_HEIGHT = 80;

export function DashboardDetail({ dashboardId, onBack }: Props) {
  const { data, isLoading, isError } = useDashboard(dashboardId);
  const renameMutation = useRenameDashboard(dashboardId);
  const deleteMutation = useDeleteDashboard();
  const addCardMutation = useAddDashboardCard(dashboardId);
  const updateLayout = useUpdateLayout(dashboardId);
  const { toast } = useToast();

  const [titleDraft, setTitleDraft] = React.useState('');
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [editorCard, setEditorCard] = React.useState<DashboardCard | null>(null);

  React.useEffect(() => {
    if (data?.dashboard.title) setTitleDraft(data.dashboard.title);
  }, [data?.dashboard.title]);

  const layoutTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const onLayoutChange = React.useCallback(
    (layout: Layout) => {
      if (layoutTimerRef.current) clearTimeout(layoutTimerRef.current);
      layoutTimerRef.current = setTimeout(() => {
        const items: LayoutItem[] = layout.map((l) => ({
          id: l.i,
          x: l.x,
          y: l.y,
          w: l.w,
          h: l.h,
        }));
        updateLayout.mutate(items);
      }, 400);
    },
    [updateLayout]
  );

  if (isLoading) {
    return (
      <Flex align="center" justify="center" className="h-full">
        <LoadingOrbit />
      </Flex>
    );
  }

  if (isError || !data) {
    return (
      <Box className="p-6">
        <Text className="text-destructive">Failed to load dashboard.</Text>
        <Button variant="outline" onPress={onBack} className="mt-4">
          Back
        </Button>
      </Box>
    );
  }

  const { dashboard, cards } = data;

  const layout: RGLLayoutItem[] = cards.map((c) => ({
    i: c.id,
    x: c.layoutX,
    y: c.layoutY,
    w: c.layoutW,
    h: c.layoutH,
    minW: 2,
    minH: 2,
  }));

  const handleRename = () => {
    if (titleDraft.trim() && titleDraft.trim() !== dashboard.title) {
      renameMutation.mutate({ title: titleDraft.trim() });
    }
    setEditingTitle(false);
  };

  const handleAddCard = () => {
    addCardMutation.mutate(
      {
        title: 'New card',
        sql: 'SELECT count(*) AS total FROM user_events',
        renderType: 'big_number',
        layoutX: 0,
        layoutW: 4,
        layoutH: 3,
      },
      {
        onError: (err) => toast.error(`Failed to add card: ${String(err)}`),
      }
    );
  };

  return (
    <Box className="flex flex-col h-full overflow-hidden relative">
      <Flex
        align="center"
        justify="between"
        gap="md"
        className="px-6 py-3 border-b border-border flex-shrink-0"
      >
        <Flex align="center" gap="sm" className="min-w-0 flex-1">
          <Button variant="ghost" size="sm" iconOnly aria-label="Back" onPress={onBack}>
            <ArrowLeft size={16} />
          </Button>
          {editingTitle ? (
            <TextField
              value={titleDraft}
              onChange={setTitleDraft}
              onBlur={handleRename}
              autoFocus
              className="max-w-md"
            />
          ) : (
            <Heading
              level={2}
              className="cursor-pointer truncate"
              onClick={() => setEditingTitle(true)}
            >
              {dashboard.title}
            </Heading>
          )}
        </Flex>
        <Flex gap="sm" align="center">
          <Button onPress={handleAddCard} isDisabled={addCardMutation.isPending}>
            <Plus size={14} /> Add card
          </Button>
          <MenuTrigger>
            <Button variant="ghost" size="sm" iconOnly aria-label="More">
              <DotsThreeVertical size={18} />
            </Button>
            <Menu
              onAction={(key) => {
                if (key === 'delete') {
                  if (window.confirm(`Delete dashboard "${dashboard.title}"?`)) {
                    deleteMutation.mutate(dashboard.id, { onSuccess: onBack });
                  }
                }
              }}
            >
              <MenuItem id="delete">
                <Trash size={14} className="mr-2" /> Delete dashboard
              </MenuItem>
            </Menu>
          </MenuTrigger>
        </Flex>
      </Flex>

      <Box className="flex-1 overflow-auto bg-muted/20 p-4">
        {cards.length === 0 ? (
          <Flex direction="column" align="center" justify="center" className="h-full text-center">
            <Heading level={3} className="mb-2">
              This dashboard is empty
            </Heading>
            <Text className="text-muted-foreground mb-4 max-w-md">
              Add a card here, or save query results from the Explore tab into this dashboard.
            </Text>
            <Button onPress={handleAddCard}>
              <Plus size={14} /> Add card
            </Button>
          </Flex>
        ) : (
          <ResponsiveGrid
            className="layout"
            layout={layout}
            cols={COLS}
            rowHeight={ROW_HEIGHT}
            margin={[12, 12]}
            draggableHandle=".drag-handle"
            draggableCancel=".no-drag"
            onLayoutChange={onLayoutChange}
            isResizable
          >
            {cards.map((card) => (
              <div key={card.id}>
                <DashboardCardWidget
                  card={card}
                  dashboardId={dashboardId}
                  onEdit={setEditorCard}
                />
              </div>
            ))}
          </ResponsiveGrid>
        )}
      </Box>

      <DashboardCardEditor
        isOpen={!!editorCard}
        card={editorCard}
        dashboardId={dashboardId}
        onClose={() => setEditorCard(null)}
      />
    </Box>
  );
}
